/* eslint-disable @typescript-eslint/camelcase */
import React from 'react';
import clsx from 'clsx';
import { connect } from 'react-redux';
import {
  CircularProgress,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import HelpIcon from '@material-ui/icons/Help';
import WarningIcon from '@material-ui/icons/Warning';
import i18n from 'i18n';
import ApplicationState from 'store/interfaces';
import { getInstanceObservabilityRequest } from 'modules/environments/actions';
import {
  InstanceObservabilityEvent,
  InstanceObservabilityState,
  InstanceObservabilityStatus,
} from 'modules/environments/interfaces';
import palette from 'theme/palette';
import { dateFormatWithAt } from 'utils/helpers';
import {
  EventStatusVariant,
  getEventDescriptionKey,
  getEventStatusLabelKey,
  getEventStatusVariant,
  sortEventsBySeverity,
} from './instanceObservabilityHelpers';
import styles from '../styles';

const useStyles = makeStyles(styles);

const statusLabelKey: Record<InstanceObservabilityStatus, string> = {
  healthy: 'INSTANCE_OBSERVABILITY.STATUS_HEALTHY',
  warning: 'INSTANCE_OBSERVABILITY.STATUS_WARNING',
  critical: 'INSTANCE_OBSERVABILITY.STATUS_CRITICAL',
};

const getEventLabelKey = (type: string): string =>
  `INSTANCE_OBSERVABILITY.EVENTS.${type}`;

export interface OwnProps {
  instanceUuid: string;
}

interface StateProps {
  instanceObservability: InstanceObservabilityState;
}

interface DispatchProps {
  getInstanceObservabilityRequest: (payload: {
    instanceUuid: string;
    preserveExisting?: boolean;
  }) => void;
}

export type Props = OwnProps & StateProps & DispatchProps;

export const InstanceObservabilityModal = ({
  instanceUuid,
  instanceObservability,
  getInstanceObservabilityRequest: fetchObservability,
}: Props) => {
  const classes = useStyles();
  const { loading, error, data } = instanceObservability;

  const [expandedEvents, setExpandedEvents] = React.useState<Set<string>>(
    new Set()
  );

  const toggleEventExpanded = (type: string) => {
    setExpandedEvents(previous => {
      const next = new Set(previous);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  React.useEffect(() => {
    if (instanceUuid) {
      fetchObservability({ instanceUuid, preserveExisting: false });
    }
  }, [instanceUuid, fetchObservability]);

  type StatusPillClasses = Record<
    'pill' | 'icon' | 'label',
    Record<EventStatusVariant, string>
  >;

  const renderStatusPill = (
    variant: EventStatusVariant,
    label: string,
    pillClasses: StatusPillClasses
  ) => {
    const { pill, icon, label: labelClass } = pillClasses;

    if (variant === 'healthy') {
      return (
        <Grid item className={pill.healthy}>
          <CheckCircleIcon className={icon.healthy} />
          <Typography className={labelClass.healthy}>{label}</Typography>
        </Grid>
      );
    }

    if (variant === 'critical') {
      return (
        <Grid item className={pill.critical}>
          <ErrorIcon className={icon.critical} />
          <Typography className={labelClass.critical}>{label}</Typography>
        </Grid>
      );
    }

    if (variant === 'warning') {
      return (
        <Grid item className={pill.warning}>
          <WarningIcon className={icon.warning} />
          <Typography className={labelClass.warning}>{label}</Typography>
        </Grid>
      );
    }

    return (
      <Grid item className={pill.notReported}>
        <HelpIcon className={icon.notReported} />
        <Typography className={labelClass.notReported}>{label}</Typography>
      </Grid>
    );
  };

  const bannerPillClasses: StatusPillClasses = {
    pill: {
      healthy: classes.observabilityStatusPillHealthy,
      warning: classes.observabilityStatusPillWarning,
      critical: classes.observabilityStatusPillCritical,
      notReported: classes.observabilityStatusPillNotReported,
    },
    icon: {
      healthy: classes.observabilityStatusIconHealthy,
      warning: classes.observabilityStatusIconWarning,
      critical: classes.observabilityStatusIconCritical,
      notReported: classes.observabilityStatusIconNotReported,
    },
    label: {
      healthy: classes.observabilityStatusLabelHealthy,
      warning: classes.observabilityStatusLabelWarning,
      critical: classes.observabilityStatusLabelCritical,
      notReported: classes.observabilityStatusLabelNotReported,
    },
  };

  const eventBadgePillClasses: StatusPillClasses = {
    pill: {
      healthy: classes.observabilityEventBadgePillHealthy,
      warning: classes.observabilityEventBadgePillWarning,
      critical: classes.observabilityEventBadgePillCritical,
      notReported: classes.observabilityEventBadgePillNotReported,
    },
    icon: {
      healthy: classes.observabilityEventBadgeIconHealthy,
      warning: classes.observabilityEventBadgeIconWarning,
      critical: classes.observabilityEventBadgeIconCritical,
      notReported: classes.observabilityEventBadgeIconNotReported,
    },
    label: {
      healthy: classes.observabilityEventBadgeLabelHealthy,
      warning: classes.observabilityEventBadgeLabelWarning,
      critical: classes.observabilityEventBadgeLabelCritical,
      notReported: classes.observabilityEventBadgeLabelNotReported,
    },
  };

  const renderHealthStatus = () => {
    if (!data) return <Grid />;

    const { status } = data;
    const label = i18n.t(statusLabelKey[status] || statusLabelKey.warning);

    return renderStatusPill(status, label, bannerPillClasses);
  };

  const renderEventStatusBadge = (event: InstanceObservabilityEvent) => {
    const variant = getEventStatusVariant(event.status);
    const label = i18n.t(getEventStatusLabelKey(event.status));

    return renderStatusPill(variant, label, eventBadgePillClasses);
  };

  const getEventDescription = (event: InstanceObservabilityEvent): string => {
    if (!event.description) {
      return i18n.t('INSTANCE_OBSERVABILITY.EVENT_NO_DATA_DESCRIPTION');
    }

    return (
      i18n.t(getEventDescriptionKey(event.description), { defaultValue: '' }) ||
      event.description
    );
  };

  if (loading) {
    return (
      <Grid container justify="center" className={classes.observabilityLoading}>
        <CircularProgress size={45} />
      </Grid>
    );
  }

  if (error && !data) {
    return (
      <Grid
        container
        direction="column"
        alignItems="center"
        style={{ padding: '48px 16px' }}
      >
        <ErrorIcon style={{ fontSize: 40, opacity: 0.45, marginBottom: 16 }} />
        <Typography variant="body2" color="textSecondary" align="center">
          {i18n.t('INSTANCE_OBSERVABILITY.LOAD_ERROR')}
        </Typography>
      </Grid>
    );
  }

  if (!data) {
    return null;
  }

  const sortedEvents = sortEventsBySeverity(data.events ?? []);

  return (
    <Grid container data-testid="autosky-instance-observability-modal">
      {error && data && (
        <Grid item xs={12} style={{ marginBottom: 16 }}>
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ fontSize: 12 }}
          >
            {i18n.t('INSTANCE_OBSERVABILITY.REFRESH_ERROR')}
          </Typography>
        </Grid>
      )}

      <Grid
        container
        justify="center"
        className={classes.observabilityHealthBanner}
      >
        {renderHealthStatus()}
      </Grid>

      {data.updated_at && (
        <Grid container justify="flex-end">
          <Typography
            variant="caption"
            component="span"
            className={classes.observabilityLastUpdated}
          >
            {i18n.t('INSTANCE_OBSERVABILITY.LAST_UPDATED')}:{' '}
            {dateFormatWithAt(data.updated_at)}
          </Typography>
        </Grid>
      )}

      {sortedEvents.length > 0 && (
        <TableContainer className={classes.observabilityEventsTable}>
          <Table className={classes.observabilityTable}>
            <TableHead>
              <TableRow className={classes.observabilityTableHeaderRow}>
                <TableCell
                  className={clsx(
                    classes.observabilityTableHeaderCell,
                    classes.observabilityTableColIcon
                  )}
                />
                <TableCell
                  className={clsx(
                    classes.observabilityTableHeaderCell,
                    classes.observabilityTableColStatus
                  )}
                >
                  {i18n.t('INSTANCE_OBSERVABILITY.TABLE_HEADER_STATUS')}
                </TableCell>
                <TableCell className={classes.observabilityTableHeaderCell}>
                  {i18n.t('INSTANCE_OBSERVABILITY.TABLE_HEADER_EVENT')}
                </TableCell>
                <TableCell
                  className={clsx(
                    classes.observabilityTableHeaderCell,
                    classes.observabilityTableColUpdated
                  )}
                >
                  {i18n.t('INSTANCE_OBSERVABILITY.TABLE_HEADER_UPDATED')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedEvents.map(event => {
                const isExpanded = expandedEvents.has(event.type);

                return (
                  <React.Fragment key={event.type}>
                    <TableRow
                      hover
                      className={classes.observabilityTableRow}
                      onClick={() => toggleEventExpanded(event.type)}
                      aria-expanded={isExpanded}
                      data-testid={`observability-event-${event.type}`}
                    >
                      <TableCell className={classes.observabilityTableCell}>
                        <IconButton
                          size="small"
                          aria-label={
                            isExpanded
                              ? 'Collapse event details'
                              : 'Expand event details'
                          }
                        >
                          <ExpandMoreIcon
                            className={
                              isExpanded
                                ? classes.observabilityExpandIconOpen
                                : classes.observabilityExpandIcon
                            }
                          />
                        </IconButton>
                      </TableCell>
                      <TableCell className={classes.observabilityTableCell}>
                        {renderEventStatusBadge(event)}
                      </TableCell>
                      <TableCell className={classes.observabilityTableCell}>
                        {i18n.t(getEventLabelKey(event.type))}
                      </TableCell>
                      <TableCell className={classes.observabilityTableCell}>
                        {event.last_updated_at
                          ? dateFormatWithAt(event.last_updated_at)
                          : '—'}
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow className={classes.observabilityTableDetailRow}>
                        <TableCell
                          style={{
                            padding: 0,
                            borderBottom: `1px solid ${palette.grey200}`,
                          }}
                        />
                        <TableCell
                          colSpan={3}
                          style={{
                            padding: 0,
                            borderBottom: `1px solid ${palette.grey200}`,
                          }}
                        >
                          <div className={classes.observabilityEventDetail}>
                            <div
                              className={classes.observabilityEventDetailFull}
                            >
                              <Typography
                                component="div"
                                className={
                                  classes.observabilityEventDetailLabel
                                }
                              >
                                {i18n.t(
                                  'INSTANCE_OBSERVABILITY.DESCRIPTION_LABEL'
                                )}
                              </Typography>
                              <Typography
                                component="div"
                                className={
                                  classes.observabilityEventDetailValue
                                }
                              >
                                {getEventDescription(event)}
                              </Typography>
                            </div>
                            <div
                              className={classes.observabilityEventDetailHalf}
                            >
                              <Typography
                                component="div"
                                className={
                                  classes.observabilityEventDetailLabel
                                }
                              >
                                {i18n.t('INSTANCE_OBSERVABILITY.LAST_UPDATED')}
                              </Typography>
                              <Typography
                                component="div"
                                className={
                                  classes.observabilityEventDetailValue
                                }
                              >
                                {event.last_updated_at
                                  ? dateFormatWithAt(event.last_updated_at)
                                  : '—'}
                              </Typography>
                            </div>
                            <div
                              className={classes.observabilityEventDetailHalf}
                            >
                              <Typography
                                component="div"
                                className={
                                  classes.observabilityEventDetailLabel
                                }
                              >
                                {i18n.t(
                                  'INSTANCE_OBSERVABILITY.IDENTIFIER_LABEL'
                                )}
                              </Typography>
                              <Typography
                                component="div"
                                className={
                                  classes.observabilityEventDetailValueMono
                                }
                              >
                                {event.type}
                              </Typography>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Grid>
  );
};

const mapStateToProps = ({
  environments: { instanceObservability },
}: ApplicationState): StateProps => ({
  instanceObservability,
});

export default connect(mapStateToProps, { getInstanceObservabilityRequest })(
  InstanceObservabilityModal
);
